import { useState } from 'react';
import { useWatch, type UseFormReturn } from 'react-hook-form';
import { FeatureFlag, type OrderDetailBlGroup } from '@metycle/shared';
import { useFeatureFlag } from '@/lib/feature-flags';
import { CoveragePicker } from '../components/coverage-picker';
import { coverageRowsFromBls, prefillByQuantity } from '../components/prefill-by-quantity';
import {
  useCoverageState,
  type CoverageBl,
  type CoverageState,
} from '../components/use-coverage-state';
import type { AdvanceSubmitValues } from './downpayment-invoice-schema';

export interface CreateCoverage {
  readonly active: boolean;
  readonly rows: CoverageBl[];
  readonly currency: string;
  readonly state: CoverageState;
  // With the allocation editor on, the shares may deliberately leave part of
  // the pool unallocated at create — Save stays enabled, with a warning.
  readonly allowUnderAllocation: boolean;
}

// The create dialog's coverage state: which BLs the new advance covers and each
// one's share, prefilled quantity-ratio from the total. Rows and prefill snap
// once at mount (an advance has no consumed floors yet); the total is watched
// so the pill re-validates as the operator edits the amount.
export function useCreateCoverage({
  orderBls,
  form,
  enabled,
}: Readonly<{
  orderBls: readonly OrderDetailBlGroup[];
  form: UseFormReturn<AdvanceSubmitValues>;
  enabled: boolean;
}>): CreateCoverage {
  const allowUnderAllocation = useFeatureFlag(FeatureFlag.INVOICING_ORDER_DP_ALLOCATION);
  const [rows] = useState<CoverageBl[]>(() => coverageRowsFromBls(orderBls));
  const [prefill] = useState<Map<string, string>>(() =>
    prefillByQuantity(rows, form.getValues('total_amount') || 0),
  );
  const total = useWatch({ control: form.control, name: 'total_amount' }) || 0;
  const currency = useWatch({ control: form.control, name: 'currency' });
  const state = useCoverageState(rows, total, prefill, allowUnderAllocation ? 'atMost' : 'exact');
  return { active: enabled && rows.length > 0, rows, currency, state, allowUnderAllocation };
}

// The create-time "Coverage & split" panel. All live BLs are pre-selected;
// deselect any this advance does not cover, then set each share. Save gates on
// the shares reconciling to the total — exactly, or at most when part of the
// pool may stay unallocated.
export function CreateCoverageSection({ coverage }: Readonly<{ coverage: CreateCoverage }>) {
  return (
    <div className="space-y-2">
      <h4 className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
        Coverage &amp; split
      </h4>
      <p className="text-muted-foreground text-xs">
        All live BLs are pre-selected. Deselect any this advance does not cover, then set each
        BL&apos;s share.{' '}
        {coverage.allowUnderAllocation
          ? 'Shares may stay below the total; the rest of the pool simply stays unallocated.'
          : 'The shares must add up to the total before you can save.'}
      </p>
      <CoveragePicker rows={coverage.rows} currency={coverage.currency} state={coverage.state} />
      {coverage.allowUnderAllocation && coverage.state.underAllocated && (
        <p className="text-xs text-amber-600 dark:text-amber-400" role="status">
          Part of the downpayment stays unallocated. You can save — assign the remainder later from
          the order&apos;s Downpayments card.
        </p>
      )}
    </div>
  );
}
